import { api, ApiError, ORG_ROLES } from '../lib/api.js';
import { el, render, fmtDate, fmtDateOnly, fmtRelative, roleLabel, sortableHeader, copy, memberStatusBadges } from '../lib/dom.js';
import { canWrite } from '../lib/session.js';
import {
  icon,
  art,
  button,
  badge,
  roleChip,
  avatar,
  card,
  kpiCard,
  progressBar,
  usageTone,
  usageRow,
  infoList,
  tabs,
  searchField,
  actionMenu,
  confirmDialog,
  typedConfirm,
  openDrawer,
  renderPagination,
  skeletonRows,
  emptyState,
  errorState,
  fullRow,
  toast,
  toastError,
} from '../lib/ui.js';
import { orgStatusBadge } from '../lib/status.js';
import { credentialsPanel } from './credentials-panel.js';
import { inviteScheduleControl } from './invite-schedule-fields.js';
import { trialScheduleControl, accessTimezoneSelect, defaultAccessTimezone } from './trial-schedule-fields.js';
import { scheduledInvitationsPanel } from './scheduled-invitation-status.js';
import { projectsTable } from './projects-table.js';
import { membershipActions } from './membership-actions.js';
import { wallPartsInZone, timezoneLabel } from '../lib/timezone.js';
import { NAME_PATTERN_SOURCE, isValidName, NAME_INVALID_MESSAGE } from '../lib/validation.js';

// ---------------------------------------------------------------------------
// Organisation detail — header, KPI cards, and tabs: Overview, Members,
// Projects, Activity, and (for write sessions) Trial & settings.
//
// The Members/Projects/Activity panels are built once and keep their own
// state (page, sort, filters); an org-level change only re-renders the header,
// KPIs, Overview and Settings, so paging a member list is never reset by
// saving a limit.
// ---------------------------------------------------------------------------

const MEMBER_PAGE_SIZE = 50;

/**
 * The timezone an org's Trial / CMAI Core dates are entered and shown in
 * (OrgConfig.defaultTimezone). Orgs created before access timezones existed
 * have none; their dates were stored as UTC midnight, so UTC shows the
 * intended calendar day.
 */
const accessTz = (org) => org.orgConfig?.defaultTimezone || 'UTC';

/** The calendar date (yyyy-MM-dd) an instant falls on in `timeZone`, for a date input. */
function dateInZone(iso, timeZone) {
  return iso ? (wallPartsInZone(iso, timeZone)?.date ?? '') : '';
}

function errMessage(err, fallback) {
  return err instanceof ApiError ? err.message : fallback;
}

export function orgDetailView({ orgId, tab = 'overview', navigate, setCrumbs }) {
  const root = el('div', { class: 'stack' });
  const headSlot = el('div', { class: 'detail-head' });
  const kpiSlot = el('div', { class: 'kpi-row' });
  const noticeSlot = el('div', {});
  const tabsSlot = el('div', {});
  const panelSlot = el('div', {});
  const state = { org: null, tab };

  // --- lazily-built, stateful panels -----------------------------------------
  let members = null;
  let projects = null;
  let activity = null;

  const TAB_IDS = ['overview', 'members', 'projects', 'activity', 'settings'];

  function visibleTabs(org) {
    return [
      { id: 'overview', label: 'Overview' },
      { id: 'members', label: 'Members', count: org.memberCount },
      { id: 'projects', label: 'Projects', count: org.projectCount },
      { id: 'activity', label: 'Activity' },
      canWrite() && org.deletedAt === null ? { id: 'settings', label: 'Trial & settings' } : null,
    ].filter(Boolean);
  }

  function selectTab(id) {
    state.tab = id;
    // replaceState, not location.hash: keeps the tab in the URL (shareable,
    // survives refresh) without firing hashchange and rebuilding the page.
    history.replaceState(null, '', `#/orgs/${orgId}${id === 'overview' ? '' : `?tab=${id}`}`);
    renderTabs();
    renderPanel();
  }

  function renderTabs() {
    const items = visibleTabs(state.org);
    if (!items.some((t) => t.id === state.tab)) state.tab = 'overview';
    render(tabsSlot, tabs(items, state.tab, selectTab));
  }

  function renderPanel() {
    const org = state.org;
    if (state.tab === 'members') {
      members ??= membersPanel();
      render(panelSlot, members.root);
    } else if (state.tab === 'projects') {
      projects ??= projectsTable({
        fetchPage: (page, pageSize) => api.listOrgProjects(orgId, { page, pageSize }),
        navigate,
        emptyText: 'This organisation has not created any projects yet.',
      });
      render(panelSlot, projects.root);
    } else if (state.tab === 'activity') {
      activity ??= activityPanel({ pageSize: 25, scroll: true });
      render(panelSlot, card({ title: 'Activity', subtitle: 'Everything recorded for this organisation, newest first.', body: activity.root }));
    } else if (state.tab === 'settings') {
      render(panelSlot, settingsPanel(org));
    } else {
      render(panelSlot, overviewPanel(org));
    }
  }

  // --- header ------------------------------------------------------------------

  async function setOrgStatus(isActive) {
    const org = state.org;
    const ok = await confirmDialog({
      tone: isActive ? 'green' : 'red',
      iconName: isActive ? 'check-circle' : 'menu-deactivate',
      title: isActive ? `Activate ${org.name}?` : `Deactivate ${org.name}?`,
      text: isActive
        ? 'Members can use the app again on their next request.'
        : 'Every user in this organisation will be locked out of the app on their next request. Memberships, accounts and data are untouched, and this is reversed by "Activate".',
      confirmLabel: isActive ? 'Activate' : 'Deactivate',
      onConfirm: () => api.setOrgStatus(orgId, isActive),
    });
    if (ok) {
      toast(`${org.name} ${isActive ? 'activated' : 'deactivated'}.`);
      await refresh();
    }
  }

  async function restoreOrg() {
    const org = state.org;
    let result = null;
    const ok = await confirmDialog({
      tone: 'green',
      iconName: 'check-circle',
      title: `Restore ${org.name}?`,
      text:
        'Memberships removed by the deletion are reinstated. Members who had been removed individually beforehand ' +
        'stay removed, and join links cancelled by the deletion are not revived — resend those.',
      confirmLabel: 'Restore organisation',
      onConfirm: async () => {
        result = await api.restoreOrg(orgId);
      },
    });
    if (!ok) return;
    toast(`${org.name} restored with ${result?.membersRestored ?? 0} member(s).`);
    // An org restored with no OWNER cannot publish config or approve exceptions
    // in the tenant app, and nothing in the product surfaces that — so say it here.
    if (result && result.hasOwner === false) {
      toast(`${result.name} has no active OWNER. Add one from Members, or the tenant cannot be administered in the app.`, { tone: 'error', timeout: 10000 });
    }
    await refresh();
    members?.reload();
  }

  function renderHeader(org) {
    const deleted = org.deletedAt !== null;
    // The status pill already says "Trial" (or "Trial expired") for a trial
    // org; when it says "Inactive" instead, the plan would be hidden.
    const showTrialPlan = org.plan === 'TRIAL' && !deleted && !org.isActive;
    const copyBtn = el(
      'button',
      {
        type: 'button',
        class: 'copy-btn',
        title: "Copy this organisation's internal ID to the clipboard, for support tickets and logs.",
        onclick: async (e) => {
          const ok = await copy(org.id);
          e.target.textContent = ok ? 'Copied' : 'Copy failed';
          setTimeout(() => (e.target.textContent = 'Copy'), 1800);
        },
      },
      'Copy',
    );

    render(
      headSlot,
      el(
        'button',
        { type: 'button', class: 'back-link', title: 'Back to the organisations list. Nothing on this page is changed.', onclick: () => navigate('/orgs') },
        icon('arrow-left'),
        'Back to Organisations',
      ),
      el(
        'div',
        { class: 'detail-head__row' },
        el(
          'div',
          { class: 'detail-head__title' },
          el('h1', {}, org.name),
          orgStatusBadge(org),
          showTrialPlan ? badge('Trial plan', 'amber') : null,
          el('span', { class: 'role-chip' }, org.configMode === 'ADVANCED' ? 'Advanced config' : 'Basic config'),
        ),
        canWrite()
          ? el(
              'div',
              { class: 'detail-head__actions' },
              deleted
                ? button('Restore organisation', { variant: 'primary', iconName: 'check-circle', onClick: () => void restoreOrg() })
                : [
                    button('Add member', { variant: 'primary', iconName: 'plus', onClick: () => openAddMemberDrawer(), title: 'Give someone access to this organisation.' }),
                    org.isActive
                      ? button('Deactivate', {
                          variant: 'danger-outline',
                          iconName: 'shield-alert',
                          onClick: () => void setOrgStatus(false),
                          title: `Lock every member of ${org.name} out of the app on their next request. Reversible.`,
                        })
                      : button('Activate', { variant: 'primary', iconName: 'check-circle', onClick: () => void setOrgStatus(true) }),
                  ],
            )
          : null,
      ),
      el(
        'p',
        { class: 'detail-meta' },
        'Created: ',
        el('strong', {}, fmtDateOnly(org.createdAt)),
        el('span', { class: 'detail-meta__sep' }, '•'),
        'Customer ID: ',
        el('strong', { class: 'mono' }, org.customerId || '—'),
        el('span', { class: 'detail-meta__sep' }, '•'),
        'Organisation ID: ',
        el('strong', { class: 'mono' }, org.id),
        copyBtn,
      ),
    );
  }

  function renderKpis(org) {
    const pct = (a, b) => (b > 0 ? (a / b) * 100 : a > 0 ? 100 : 0);
    const projectsPct = pct(org.projectCount, org.maxProjects);
    const cocoPct = pct(org.cocoPromptsUsed, org.cocoPromptLimit);
    const seatsPct = pct(org.memberCount, org.maxUsers);
    render(
      kpiSlot,
      kpiCard({
        label: 'Members',
        value: el('span', {}, String(org.memberCount), el('small', {}, ` / ${org.maxUsers}`)),
        iconName: 'kpi-users',
        tone: 'green',
        progress: progressBar(seatsPct, { tone: usageTone(seatsPct) }),
        meta: `${Math.max(0, org.maxUsers - org.memberCount)} more allowed`,
      }),
      kpiCard({
        label: 'Projects',
        value: el('span', {}, String(org.projectCount), el('small', {}, ` / ${org.maxProjects}`)),
        iconName: 'kpi-projects',
        tone: 'blue',
        progress: progressBar(projectsPct, { tone: usageTone(projectsPct) }),
        meta: `${Math.max(0, org.maxProjects - org.projectCount)} more allowed`,
      }),
      kpiCard({
        label: 'Re-validations left',
        value: String(org.revalidationsRemaining),
        iconName: 'kpi-ai',
        tone: 'indigo',
        meta: 'Schedule re-validations this organisation can still run',
      }),
      kpiCard({
        label: 'Coco AI credits',
        value: el('span', {}, String(org.cocoPromptsUsed), el('small', {}, ` / ${org.cocoPromptLimit}`)),
        iconName: 'kpi-building',
        tone: 'amber',
        progress: progressBar(cocoPct, { tone: usageTone(cocoPct) }),
        meta: `${org.cocoPromptsLeft} credits left`,
      }),
    );
  }

  function renderNotice(org) {
    if (org.deletedAt !== null) {
      render(
        noticeSlot,
        card({
          className: 'danger-zone',
          body: el(
            'div',
            { class: 'danger-row' },
            el('div', {}, el('p', { class: 'card__title' }, 'This organisation is deleted'), el('p', { class: 'danger-row__desc' }, `Deleted on ${fmtDate(org.deletedAt)}. Its data is retained and it can be restored.`)),
          ),
        }),
      );
    } else if (org.trialExpired) {
      render(
        noticeSlot,
        card({
          className: 'danger-zone',
          body: el(
            'div',
            { class: 'danger-row' },
            el(
              'div',
              {},
              el('p', { class: 'card__title' }, 'Trial expired — members are locked out'),
              el('p', { class: 'danger-row__desc' }, 'Every user is blocked until the trial end date is moved into the future (Trial & settings). Activate/Deactivate does not undo this.'),
            ),
            canWrite() ? button('Extend trial', { variant: 'primary', onClick: () => selectTab('settings') }) : null,
          ),
        }),
      );
    } else if (org.planExpired) {
      render(
        noticeSlot,
        card({
          className: 'danger-zone',
          body: el(
            'div',
            { class: 'danger-row' },
            el(
              'div',
              {},
              el('p', { class: 'card__title' }, 'CMAI Core access ended — members are locked out'),
              el('p', { class: 'danger-row__desc' }, 'Every user is blocked until the organisation is renewed (Trial & settings). Activate/Deactivate does not undo this.'),
            ),
            canWrite() ? button('Renew plan', { variant: 'primary', onClick: () => selectTab('settings') }) : null,
          ),
        }),
      );
    } else if (org.trialNotStarted) {
      render(
        noticeSlot,
        card({
          className: 'danger-zone',
          body: el(
            'div',
            { class: 'danger-row' },
            el(
              'div',
              {},
              el('p', { class: 'card__title' }, `Trial starts ${fmtDateOnly(org.trialStartsAt, accessTz(org))} — members can’t sign in yet`),
              el('p', { class: 'danger-row__desc' }, 'Every user is blocked until the trial start date. Move the start date in Trial & settings to open access sooner.'),
            ),
            canWrite() ? button('Change dates', { variant: 'primary', onClick: () => selectTab('settings') }) : null,
          ),
        }),
      );
    } else if (org.planNotStarted) {
      render(
        noticeSlot,
        card({
          className: 'danger-zone',
          body: el(
            'div',
            { class: 'danger-row' },
            el(
              'div',
              {},
              el('p', { class: 'card__title' }, `CMAI Core access starts ${fmtDateOnly(org.planActivatedAt, accessTz(org))} — members can’t sign in yet`),
              el('p', { class: 'danger-row__desc' }, 'Every user is blocked until the start date. Move the start date in Trial & settings to open access sooner.'),
            ),
            canWrite() ? button('Change dates', { variant: 'primary', onClick: () => selectTab('settings') }) : null,
          ),
        }),
      );
    } else if (org.defaultScheduleStandard === null) {
      render(noticeSlot, el('p', { class: 'note note-warn' }, 'This organisation has no default schedule standard. It was probably created outside this portal — AI validation will have no rules to check against.'));
    } else {
      render(noticeSlot);
    }
  }

  // --- Overview tab ---------------------------------------------------------------

  function overviewPanel(org) {
    const owners = org.owners ?? [];
    const primary = owners[0] ?? null;
    const std = org.defaultScheduleStandard;

    const ownerCard = card({
      title: 'Primary owner',
      actions: primary ? [button('View profile', { variant: 'ghost', size: 'sm', onClick: () => navigate(`/users/${primary.userId}`) })] : [],
      body: primary
        ? el(
            'div',
            { class: 'stack', style: 'gap:12px' },
            el('div', { class: 'person' }, avatar(primary.fullName, { size: 48 }), el('div', {}, el('p', { class: 'person__name' }, primary.fullName), el('p', { class: 'person__sub' }, primary.email))),
            infoList([
              ['Last login', primary.lastLoginAt ? fmtRelative(primary.lastLoginAt) : 'Never'],
              ['Status', el('span', { class: 'cell-badges', style: 'justify-content:flex-end' }, memberStatusBadges({ ...primary, removedAt: null, accountDeletedAt: null }))],
              ['Joined', fmtDateOnly(primary.joinedAt)],
              owners.length > 1 ? ['Other owners', owners.slice(1).map((o) => o.fullName).join(', ')] : null,
            ]),
          )
        : el('p', { class: 'note note-warn' }, 'No active OWNER — add one from Members, or the tenant cannot be administered in the app.'),
    });

    const usageCard = card({
      title: 'Usage limits',
      body: el(
        'div',
        { class: 'stack', style: 'gap:14px' },
        usageRow({ label: 'Members', used: org.memberCount, limit: org.maxUsers }),
        usageRow({ label: 'Projects', used: org.projectCount, limit: org.maxProjects }),
        usageRow({ label: 'Coco AI credits', used: org.cocoPromptsUsed, limit: org.cocoPromptLimit }),
        infoList([['Re-validations remaining', String(org.revalidationsRemaining)]]),
      ),
    });

    const recent = activityPanel({ pageSize: 5, preview: true });

    return el(
      'div',
      { class: 'split' },
      el(
        'div',
        { class: 'stack' },
        card({
          title: 'Organisation information',
          body: infoList([
            [
              'Plan',
              org.plan === 'CMAI_CORE'
                ? badge('CMAI Core', 'indigo', { dot: true })
                : badge('Trial', 'amber', { dot: true }),
            ],
            org.plan === 'CMAI_CORE'
              ? [
                  'CMAI Core access',
                  org.planExpiresAt === null
                    ? 'No access window recorded'
                    : org.planNotStarted
                      ? `Starts ${fmtDateOnly(org.planActivatedAt, accessTz(org))} · until ${fmtDateOnly(org.planExpiresAt, accessTz(org))}`
                      : `${org.planExpired ? 'Ended' : 'Until'} ${fmtDateOnly(org.planExpiresAt, accessTz(org))}`,
                ]
              : null,
            ['Industry', org.industry],
            ['Country', org.country],
            ['Customer ID', org.customerId ? el('span', { class: 'mono' }, org.customerId) : null],
            ['Config mode', org.configMode === 'ADVANCED' ? 'Advanced' : 'Basic'],
            ['Data residency', org.orgConfig?.dataResidency ? `${org.orgConfig.dataResidency} server` : null],
            ['Default timezone', org.orgConfig?.defaultTimezone ? `${timezoneLabel(org.orgConfig.defaultTimezone)} — Trial / CMAI Core dates are in this timezone` : 'Not set (dates shown in UTC)'],
            ['Trial window', org.trialStartsAt && org.trialEndsAt ? `${fmtDateOnly(org.trialStartsAt, accessTz(org))} – ${fmtDateOnly(org.trialEndsAt, accessTz(org))}` : 'Not on a trial'],
            ['Org config', org.orgConfig ? `v${org.orgConfig.version} · ${org.orgConfig.status}` : 'Not created'],
            ['Last updated', fmtDate(org.updatedAt)],
          ]),
        }),
        card({
          title: 'Schedule standard',
          body:
            std === null
              ? el('p', { class: 'note note-warn' }, 'No default schedule standard seeded — AI validation will have no rules to check against.')
              : infoList([
                  ['Standard', std.title],
                  ['Rules', String(std.ruleCount)],
                  ['Status', badge(std.status, std.status === 'ACTIVE' ? 'green' : 'gray')],
                ]),
        }),
        (org.scheduledInvitations ?? []).length > 0
          ? card({ title: 'Scheduled invitations', body: scheduledInvitationsPanel(org.scheduledInvitations) })
          : null,
      ),
      el(
        'div',
        { class: 'stack' },
        ownerCard,
        usageCard,
        card({
          title: 'Recent activity',
          actions: [button('View all', { variant: 'ghost', size: 'sm', onClick: () => selectTab('activity') })],
          body: recent.root,
        }),
      ),
    );
  }

  // --- Activity (timeline) --------------------------------------------------------

  /**
   * Self-loading activity timeline. `preview` shows one short page with no
   * "Load more"; the Activity tab pages through everything.
   */
  function activityPanel({ pageSize, preview = false, scroll = false }) {
    const st = { page: 0, total: null, loading: false, entries: [] };
    const listSlot = el('div', { class: scroll ? 'timeline-scroll' : '' });
    const footerSlot = el('div', {});

    function renderRows() {
      if (st.entries.length === 0) {
        render(listSlot, el('p', { class: 'section-hint' }, st.total === null ? 'Loading…' : 'No recorded activity yet.'));
        return;
      }
      render(
        listSlot,
        el(
          'div',
          { class: 'timeline' },
          st.entries.map((a, i) =>
            el(
              'div',
              { class: 'timeline__item' },
              el('div', { class: 'timeline__node' }, art(i === st.entries.length - 1 && (preview || st.entries.length >= st.total) ? 'timeline-node-last' : 'timeline-node')),
              el(
                'div',
                { class: 'timeline__details' },
                el('p', { class: 'timeline__text' }, a.text),
                el('p', { class: 'timeline__meta' }, fmtDate(a.createdAt), a.actorEmail ? [' · ', el('strong', {}, a.actorEmail)] : null),
              ),
            ),
          ),
        ),
      );
    }

    function renderFooter() {
      if (preview) {
        render(footerSlot);
        return;
      }
      const shown = st.entries.length;
      const hasMore = st.total !== null && shown < st.total;
      render(
        footerSlot,
        hasMore
          ? button(st.loading ? 'Loading…' : 'Load more', {
              size: 'sm',
              disabled: st.loading,
              className: 'btn-block',
              title: `Fetch the next ${Math.min(pageSize, st.total - shown)} older entries.`,
              onClick: () => void loadNext(),
            })
          : st.total ? el('p', { class: 'section-hint', style: 'text-align:center' }, `${shown} of ${st.total} shown.`) : null,
      );
    }

    async function loadNext() {
      if (st.loading) return;
      st.loading = true;
      renderFooter();
      try {
        const next = st.page + 1;
        const data = await api.listOrgActivity(orgId, { page: next, pageSize });
        st.page = next;
        st.total = data.total;
        st.entries = st.entries.concat(data.entries);
      } catch (err) {
        render(listSlot, el('p', { class: 'form-error' }, errMessage(err, 'Failed to load activity.')));
        st.loading = false;
        renderFooter();
        return;
      }
      st.loading = false;
      renderRows();
      renderFooter();
    }

    renderRows();
    void loadNext();
    return { root: el('div', { class: 'stack', style: 'gap:12px' }, listSlot, footerSlot) };
  }

  // --- Members tab -----------------------------------------------------------------

  function membersPanel() {
    const st = { search: '', includeRemoved: false, sortBy: 'joinedAt', sortDir: 'desc', page: 1, total: 0 };
    const resultSlot = el('div', {});
    const tbody = el('tbody', {});
    const footer = el('div', {});
    const theadRow = el('tr', {});
    // Always shown: a read-only session still gets 'View user profile'.
    const cols = 6;

    const search = searchField({
      placeholder: 'Search members…',
      label: 'Search members by name or email',
      onSearch: (v) => {
        st.search = v;
        st.page = 1;
        void load();
      },
    });

    const removedToggle = el('input', {
      type: 'checkbox',
      onchange: (e) => {
        st.includeRemoved = e.target.checked;
        st.page = 1;
        void load();
      },
    });

    function sortHeader(label, field) {
      return sortableHeader(st, field, label, () => {
        st.page = 1;
        void load();
      });
    }

    /**
     * A member's other organisations, as a note under their status. This list
     * is scoped to one tenant, which makes a row easy to read as the whole of
     * that person's presence. Removing them here leaves every other membership
     * standing — naming the number is what lets an operator tell the two apart.
     */
    function otherOrgs(u) {
      const n = typeof u.activeOrgCount === 'number' ? u.activeOrgCount : 1;
      if (n <= 1) return null;
      return el('span', { class: 'role-chip', title: `Also a member of ${n - 1} other organisation${n === 2 ? '' : 's'}. Removing them here does not affect those.` }, `${n} orgs`);
    }

    function row(u) {
      const items = [
        { label: 'View user profile', iconName: 'menu-view', onSelect: () => navigate(`/users/${u.userId}`) },
        ...(canWrite()
          ? membershipActions(
              { ...u, orgId },
              {
                orgName: state.org?.name,
                onChanged: (message) => {
                  toast(message);
                  void load();
                  void refresh();
                },
                onCredentials: (node) => {
                  render(resultSlot, node);
                  resultSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                },
              },
            )
          : []),
      ];
      return el(
        'tr',
        {},
        el(
          'td',
          {},
          el(
            'div',
            { class: 'cell-main' },
            avatar(u.fullName, { size: 36 }),
            el('div', { class: 'cell-main__text' }, el('span', { class: 'cell-title' }, u.fullName), el('span', { class: 'cell-sub' }, u.email)),
          ),
        ),
        el('td', {}, roleChip(roleLabel(u.role))),
        el(
          'td',
          {},
          el(
            'div',
            { class: 'cell-badges' },
            memberStatusBadges(u),
            otherOrgs(u),
            u.mfaEnabled ? el('span', { class: 'role-chip' }, 'MFA') : null,
            u.authProvider === 'AZURE_AD' ? el('span', { class: 'role-chip' }, 'SSO') : null,
          ),
        ),
        el('td', { class: 'nowrap' }, u.lastLoginAt ? fmtRelative(u.lastLoginAt) : 'Never'),
        el('td', { class: 'nowrap' }, fmtDateOnly(u.joinedAt)),
        el('td', { class: 'col-actions' }, actionMenu(items, { label: `Actions for ${u.fullName}` })),
      );
    }

    async function load() {
      render(
        theadRow,
        sortHeader('Member', 'fullName'),
        sortHeader('Role', 'role'),
        // Status is a badge derived from several fields; membership isActive
        // is the closest single sortable column to it.
        sortHeader('Status', 'isActive'),
        sortHeader('Last login', 'lastLoginAt'),
        sortHeader('Joined', 'joinedAt'),
        el('th', { class: 'col-actions' }, ''),
      );
      render(tbody, skeletonRows(cols, 4));
      try {
        const data = await api.listUsers(orgId, {
          search: st.search,
          includeRemoved: st.includeRemoved,
          sortBy: st.sortBy,
          sortDir: st.sortDir,
          page: st.page,
          pageSize: MEMBER_PAGE_SIZE,
        });
        st.total = data.total;
        render(
          tbody,
          data.users.length === 0
            ? fullRow(cols, emptyState({ title: st.search ? 'No members match' : 'No members yet', text: st.search ? 'Try a different name or email.' : 'Add the first member of this organisation.', compact: true }))
            : data.users.map(row),
        );
        renderPagination(footer, {
          page: st.page,
          pageSize: MEMBER_PAGE_SIZE,
          total: st.total,
          noun: 'members',
          onPage: (n) => {
            st.page = n;
            void load();
          },
        });
      } catch (err) {
        render(tbody, fullRow(cols, errorState({ title: 'Failed to load members', text: errMessage(err, 'Check the API and try again.'), onRetry: () => void load() })));
        render(footer);
      }
    }

    void load();

    const rootNode = el(
      'div',
      { class: 'stack' },
      resultSlot,
      el(
        'div',
        { class: 'table-card' },
        el(
          'div',
          { class: 'card-toolbar' },
          search.root,
          el(
            'div',
            { class: 'card-toolbar__actions' },
            el('label', { class: 'checkbox-field' }, removedToggle, el('span', { class: 'box' }), 'Show removed'),
            canWrite() && state.org?.deletedAt === null ? button('Add member', { variant: 'primary', iconName: 'plus', onClick: () => openAddMemberDrawer() }) : null,
          ),
        ),
        // Says out loud what these actions are scoped to, and where the
        // account-wide ones went.
        el(
          'p',
          { class: 'card-note' },
          '"Remove" ends membership of this organisation only — the account and any other organisations it belongs to are kept. To deactivate, delete or restore the ConstructMind account itself, open the user’s profile.',
        ),
        el('div', { class: 'table-scroll table-scroll--tall' }, el('table', { class: 'table' }, el('thead', {}, theadRow), tbody)),
        footer,
      ),
    );

    return { root: rootNode, reload: load, showResult: (node) => render(resultSlot, node) };
  }

  // --- Add member drawer -------------------------------------------------------------

  function openAddMemberDrawer() {
    const org = state.org;
    const error = el('p', { class: 'form-error', role: 'alert', hidden: true });
    const email = el('input', { class: 'input', type: 'email', required: true, maxlength: '255', placeholder: 'person@client.com', autocomplete: 'off' });
    const fullName = el('input', { class: 'input', type: 'text', required: true, maxlength: '200', placeholder: 'e.g. Sarah Chen', pattern: NAME_PATTERN_SOURCE, title: NAME_INVALID_MESSAGE });
    const role = el('select', { class: 'input' }, ORG_ROLES.map((r) => el('option', { value: r, selected: r === 'PROJECT_PLANNER' }, roleLabel(r))));
    const invite = inviteScheduleControl();
    const sendEmail = el('input', { type: 'checkbox', checked: true, onchange: (e) => invite.setEnabled(e.target.checked) });
    invite.setEnabled(true);

    const submit = button('Add member', { variant: 'primary', type: 'submit' });
    const cancel = button('Cancel', { onClick: () => drawer.close() });

    const form = el(
      'form',
      {
        class: 'drawer-form',
        onsubmit: async (e) => {
          e.preventDefault();
          error.hidden = true;
          if (!isValidName(fullName.value)) {
            error.textContent = NAME_INVALID_MESSAGE;
            error.hidden = false;
            return;
          }
          submit.disabled = true;
          submit.textContent = 'Adding…';
          try {
            const result = await api.addUser(orgId, {
              email: email.value.trim().toLowerCase(),
              fullName: fullName.value.trim(),
              role: role.value,
              sendInviteEmail: sendEmail.checked,
              ...invite.getValue(),
            });
            drawer.close();
            toast(`${result.user.email} added to ${org.name}.`);
            if (state.tab !== 'members') selectTab('members');
            members?.showResult(
              credentialsPanel({
                title: `${result.user.email} added`,
                user: result.user,
                email: result.email,
                scheduledInvitation: result.scheduledInvitation,
                note: result.note,
              }),
            );
            members?.reload();
            void refresh();
          } catch (err) {
            error.textContent = errMessage(err, 'Failed to add the member.');
            error.hidden = false;
            submit.disabled = false;
            submit.textContent = 'Add member';
          }
        },
      },
      el('p', { class: 'section-hint' }, `Give someone access to ${org.name}. An existing ConstructMind user keeps their password and gains access to this organisation as well — no second account.`),
      el('div', { class: 'field' }, el('label', {}, 'Work email', el('span', { class: 'req' }, '*')), email),
      el('div', { class: 'field' }, el('label', {}, 'Full name', el('span', { class: 'req' }, '*')), fullName),
      el('div', { class: 'field' }, el('label', {}, 'Role', el('span', { class: 'req' }, '*')), role),
      el('label', { class: 'checkbox-field' }, sendEmail, el('span', { class: 'box' }), 'Send the invite email'),
      invite.root,
      org.memberCount >= org.maxUsers
        ? el('p', { class: 'note note-warn' }, `This organisation already has ${org.memberCount} of ${org.maxUsers} members — the API will refuse another until Max users is raised (Trial & settings).`)
        : null,
      error,
      el('div', { class: 'form-actions' }, cancel, submit),
    );

    const drawer = openDrawer({ title: 'Add member', body: form });
  }

  // --- Trial & settings tab ----------------------------------------------------------

  function numberControl({ title, desc, label, value, width = 130, confirmText, submitLabel = 'Update', onSave }) {
    const input = el('input', { class: 'input mono', type: 'number', min: '0', max: '100000', value: String(value) });
    const err = el('p', { class: 'form-error', role: 'alert', hidden: true });
    const submit = button(submitLabel, { variant: 'primary', type: 'submit' });
    return el(
      'div',
      { class: 'ctl' },
      el('div', {}, el('div', { class: 'ctl__title' }, title), el('div', { class: 'ctl__desc' }, desc)),
      el(
        'form',
        {
          class: 'ctl__fields',
          onsubmit: async (e) => {
            e.preventDefault();
            err.hidden = true;
            const n = Number(input.value);
            if (!Number.isInteger(n) || n < 0) {
              err.textContent = 'Enter a whole number of zero or more.';
              err.hidden = false;
              return;
            }
            const text = confirmText?.(n);
            if (text) {
              const ok = await confirmDialog({ tone: 'indigo', iconName: 'menu-edit', title: `${title}: ${n}?`, text, confirmLabel: submitLabel, onConfirm: () => onSave(n) });
              if (!ok) return;
            } else {
              submit.disabled = true;
              try {
                await onSave(n);
              } catch (ex) {
                err.textContent = errMessage(ex, 'Failed to save.');
                err.hidden = false;
                submit.disabled = false;
                return;
              }
            }
            toast(`${title} updated.`);
            await refresh();
          },
        },
        el('div', { class: 'field field--fixed', style: `width:${width}px` }, el('label', {}, label), input),
        submit,
        err,
      ),
    );
  }

  function settingsPanel(org) {
    // --- plan ---
    const CORE_DEFAULTS_TEXT = '10 projects, 10 users, 10 re-validations, 200 Coco AI credits';
    const TRIAL_DEFAULTS_TEXT = '3 projects, 3 users, 10 re-validations, 100 Coco AI credits';
    const planRow = el(
      'div',
      { class: 'ctl' },
      el(
        'div',
        {},
        el('div', { class: 'ctl__title' }, 'Plan'),
        el(
          'div',
          { class: 'ctl__desc' },
          org.plan === 'CMAI_CORE'
            ? org.planExpiresAt === null
              ? 'CMAI Core — no access window recorded (pre-existing paid org).'
              : org.planNotStarted
                ? `CMAI Core — access starts ${fmtDateOnly(org.planActivatedAt, accessTz(org))} and runs until ${fmtDateOnly(org.planExpiresAt, accessTz(org))}.`
                : `CMAI Core — access ${org.planExpired ? 'ended' : 'until'} ${fmtDateOnly(org.planExpiresAt, accessTz(org))}.`
            : 'Trial — every feature unlocked; only the limits below apply, until the trial window ends.',
        ),
      ),
      el(
        'div',
        { class: 'ctl__controls' },
        org.plan === 'CMAI_CORE' ? badge('CMAI Core', 'indigo', { dot: true }) : badge('Trial', 'amber', { dot: true }),
        button(org.plan === 'CMAI_CORE' ? 'Switch to Trial' : 'Switch to CMAI Core', {
          title: `Switches the commercial tier and resets max projects, max users, re-validations, and Coco AI credits to the new plan's defaults.`,
          onClick: async () => {
            const next = org.plan === 'CMAI_CORE' ? 'TRIAL' : 'CMAI_CORE';
            const nextLabel = next === 'CMAI_CORE' ? 'CMAI Core' : 'Trial';
            const ok = await confirmDialog({
              tone: 'indigo',
              iconName: 'kpi-building',
              title: `Switch ${org.name} to ${nextLabel}?`,
              text:
                (next === 'CMAI_CORE'
                  ? 'Ends the trial window and starts a fresh 12-month access window from today (adjust it afterwards under CMAI Core access window). '
                  : 'Clears the CMAI Core access window and starts a fresh 15-day trial from today — members are locked out when it ends unless you move the end date. ') +
                `Max projects, max users, re-validations, and Coco AI credits are ALL reset to ${nextLabel}'s defaults (${
                  next === 'CMAI_CORE' ? CORE_DEFAULTS_TEXT : TRIAL_DEFAULTS_TEXT
                }) — any manual overrides on this organisation are discarded. Set them again below afterward if needed.`,
              confirmLabel: `Switch to ${nextLabel}`,
              onConfirm: () => api.setPlan(orgId, next),
            });
            if (ok) {
              toast(`Plan switched to ${nextLabel}.`);
              await refresh();
            }
          },
        }),
      ),
    );

    // --- trial window ---
    // Dates are calendar days in the org's timezone: 00:00 on the start date
    // to 23:59 on the end date. Exact times only reopen when they differ from
    // those defaults, so a plain window stays a plain window on the next save.
    const trialTz = accessTimezoneSelect({ value: defaultAccessTimezone(org.orgConfig?.defaultTimezone, org.orgConfig?.dataResidency) });
    const savedTz = accessTz(org);
    const startParts = org.trialStartsAt ? wallPartsInZone(org.trialStartsAt, savedTz) : null;
    const endParts = org.trialEndsAt ? wallPartsInZone(org.trialEndsAt, savedTz) : null;
    const trialStart = el('input', { class: 'input mono', type: 'date', value: startParts?.date ?? '' });
    const trialEnd = el('input', { class: 'input mono', type: 'date', value: endParts?.date ?? '' });
    const trialError = el('p', { class: 'form-error', role: 'alert', hidden: true });
    const trialSchedule = trialScheduleControl();
    if (org.orgConfig?.defaultTimezone && startParts && endParts && (startParts.time !== '00:00' || endParts.time !== '23:59')) {
      trialSchedule.setValue({ trialStartTime: startParts.time, trialEndTime: endParts.time });
    }
    const trialRow = el(
      'div',
      { class: 'ctl' },
      el(
        'div',
        {},
        el('div', { class: 'ctl__title' }, 'Trial window'),
        el(
          'div',
          { class: 'ctl__desc' },
          'Move the trial start and end dates — from 00:00 on the start date to 23:59 on the end date, in the selected timezone. Members can’t sign in before the start. No email is sent.',
          org.trialNotStarted ? ' The trial hasn’t started yet — every user is blocked until the start date.' : null,
          org.trialExpired ? ' This organisation is locked out — every user is blocked until the end date is moved into the future.' : null,
        ),
      ),
      el(
        'form',
        {
          class: 'ctl__fields',
          onsubmit: async (e) => {
            e.preventDefault();
            trialError.hidden = true;
            if (!trialStart.value || !trialEnd.value) {
              trialError.textContent = 'Set both a trial start and end date.';
              trialError.hidden = false;
              return;
            }
            if (trialEnd.value < trialStart.value) {
              trialError.textContent = 'The end date can’t be before the start date.';
              trialError.hidden = false;
              return;
            }
            const ok = await confirmDialog({
              tone: 'indigo',
              iconName: 'nav-trials',
              title: `Update ${org.name}'s trial window?`,
              text:
                `${trialStart.value} 00:00 – ${trialEnd.value} 23:59 (${timezoneLabel(trialTz.value)}). ` +
                (org.plan === 'TRIAL'
                  ? 'On the Trial plan the end date is what locks members out — moving it earlier can lock them out immediately.'
                  : 'This organisation is on CMAI Core, so this date alone will not lock anyone out.'),
              confirmLabel: 'Update dates',
              onConfirm: () =>
                api.updateTrialDates(orgId, {
                  trialStartsAt: trialStart.value,
                  trialEndsAt: trialEnd.value,
                  accessTimezone: trialTz.value,
                  ...trialSchedule.getValue(),
                }),
            });
            if (ok) {
              toast('Trial window updated.');
              await refresh();
            }
          },
        },
        el('div', { class: 'field' }, el('label', {}, 'Timezone'), trialTz),
        el('div', { class: 'field' }, el('label', {}, 'Start'), trialStart),
        el('div', { class: 'field' }, el('label', {}, 'End'), trialEnd),
        button('Update dates', { variant: 'primary', type: 'submit' }),
        trialSchedule.root,
        trialError,
      ),
    );

    // --- CMAI Core access window ---
    // Only meaningful on CMAI Core; the trial window row above is its Trial
    // counterpart. Picking a start fills the end 12 months later.
    const coreTz = accessTimezoneSelect({ value: defaultAccessTimezone(org.orgConfig?.defaultTimezone, org.orgConfig?.dataResidency) });
    const coreStart = el('input', { class: 'input mono', type: 'date', value: dateInZone(org.planActivatedAt, accessTz(org)) });
    const coreEnd = el('input', { class: 'input mono', type: 'date', value: dateInZone(org.planExpiresAt, accessTz(org)) });
    coreStart.addEventListener('change', () => {
      if (!coreStart.value) return;
      // Inclusive end date (access until 23:59): 12 months from 2 Oct ends 1 Oct.
      const [y, m, d] = coreStart.value.split('-').map(Number);
      const end = new Date(Date.UTC(y, m - 1 + 12, d));
      if (end.getUTCDate() !== d) end.setUTCDate(0);
      end.setUTCDate(end.getUTCDate() - 1);
      coreEnd.value = end.toISOString().slice(0, 10);
    });
    const coreError = el('p', { class: 'form-error', role: 'alert', hidden: true });
    const coreWindowRow = el(
      'div',
      { class: 'ctl' },
      el(
        'div',
        {},
        el('div', { class: 'ctl__title' }, 'CMAI Core access window'),
        el(
          'div',
          { class: 'ctl__desc' },
          'Members can sign in from 00:00 on the start date until 23:59 on the end date, in the selected timezone. Changing the start fills the end 12 months later. No email is sent.',
          org.planNotStarted ? ' Access hasn’t started yet — every user is blocked until the start date.' : null,
          org.planExpired ? ' This organisation is locked out — every user is blocked until the end date is moved into the future.' : null,
        ),
      ),
      el(
        'form',
        {
          class: 'ctl__fields',
          onsubmit: async (e) => {
            e.preventDefault();
            coreError.hidden = true;
            if (!coreStart.value || !coreEnd.value) {
              coreError.textContent = 'Set both a start and end date.';
              coreError.hidden = false;
              return;
            }
            if (coreEnd.value < coreStart.value) {
              coreError.textContent = 'The end date can’t be before the start date.';
              coreError.hidden = false;
              return;
            }
            // "Today" in the selected timezone, not the browser's.
            const today = new Date().toLocaleDateString('en-CA', { timeZone: coreTz.value });
            const ok = await confirmDialog({
              tone: 'indigo',
              iconName: 'kpi-building',
              title: `Update ${org.name}'s CMAI Core access window?`,
              text:
                `${coreStart.value} 00:00 – ${coreEnd.value} 23:59 (${timezoneLabel(coreTz.value)}). ` +
                (coreStart.value > today
                  ? 'The start is in the future — members are locked out until then.'
                  : coreEnd.value < today
                    ? 'The end is not in the future — members are locked out immediately.'
                    : 'Members keep access until the end date. Renewal reminders re-arm against it.'),
              confirmLabel: 'Update dates',
              onConfirm: () =>
                api.updatePlanWindow(orgId, { planStartsAt: coreStart.value, planEndsAt: coreEnd.value, accessTimezone: coreTz.value }),
            });
            if (ok) {
              toast('CMAI Core access window updated.');
              await refresh();
            }
          },
        },
        el('div', { class: 'field' }, el('label', {}, 'Timezone'), coreTz),
        el('div', { class: 'field' }, el('label', {}, 'Start'), coreStart),
        el('div', { class: 'field' }, el('label', {}, 'End'), coreEnd),
        button('Update dates', { variant: 'primary', type: 'submit' }),
        coreError,
      ),
    );

    const maxProjectsRow = numberControl({
      title: 'Max projects',
      desc: 'Number of projects this organisation may create.',
      label: 'Max projects',
      value: org.maxProjects,
      width: 120,
      confirmText: (n) =>
        `Change max projects from ${org.maxProjects} to ${n}.` +
        (n < org.maxProjects ? ' This does not delete any existing projects — it only stops them creating more once they are at the new limit.' : ''),
      onSave: (n) => api.setMaxProjects(orgId, n),
    });

    const maxUsersRow = numberControl({
      title: 'Max users',
      desc: 'Number of active members this organisation may have. Existing members are never removed by lowering it — it only blocks adding another once they are at the new limit.',
      label: 'Max users',
      value: org.maxUsers,
      width: 120,
      confirmText: (n) =>
        `Change max users from ${org.maxUsers} to ${n}.` +
        (n < org.memberCount ? ` This organisation currently has ${org.memberCount} members — no one will be removed, but no one new can join until membership drops back under ${n} or the limit is raised again.` : ''),
      onSave: (n) => api.setMaxUsers(orgId, n),
    });

    // There is no separate "max" — this sets the live budget directly, so
    // raising it both increases the cap AND grants that many re-validations.
    const revalidationsRow = numberControl({
      title: 'Re-validations remaining',
      desc: 'Number of schedule re-validations this organisation currently has left. Takes effect immediately.',
      label: 'Re-validations',
      value: org.revalidationsRemaining,
      width: 140,
      onSave: (n) => api.setRevalidationsRemaining(orgId, n),
    });

    // The operator sets credits LEFT; the API stores the ceiling as used +
    // left, counting usage at save time. Enforced on every plan.
    const cocoInput = el('input', { class: 'input mono', type: 'number', min: '0', max: '100000', step: '2', value: String(org.cocoPromptsLeft) });
    const step = (d) => {
      const cur = Number(cocoInput.value);
      cocoInput.value = String(Math.max(0, (Number.isFinite(cur) ? cur : 0) + d));
    };
    const cocoError = el('p', { class: 'form-error', role: 'alert', hidden: true });
    const cocoRow = el(
      'div',
      { class: 'ctl' },
      el(
        'div',
        {},
        el('div', { class: 'ctl__title' }, 'Coco AI credits left'),
        el(
          'div',
          { class: 'ctl__desc' },
          `Credits the whole organisation can still send — ${org.cocoPromptsUsed} used so far.`,
        ),
      ),
      el(
        'form',
        {
          class: 'ctl__fields',
          onsubmit: async (e) => {
            e.preventDefault();
            cocoError.hidden = true;
            const n = Number(cocoInput.value);
            if (!Number.isInteger(n) || n < 0) {
              cocoError.textContent = 'Enter a whole number of zero or more.';
              cocoError.hidden = false;
              return;
            }
            if (n % 2 !== 0) {
              cocoError.textContent = 'Credits are spent 2 at a time — enter an even number.';
              cocoError.hidden = false;
              return;
            }
            const ok = await confirmDialog({
              tone: n === 0 ? 'red' : 'indigo',
              iconName: 'kpi-ai',
              title: `Set Coco AI credits left to ${n}?`,
              text:
                `Currently ${org.cocoPromptsLeft} credits left.` +
                (n === 0 ? ' Coco will stop answering them immediately.' : ''),
              confirmLabel: 'Update credits',
              onConfirm: () => api.setCocoPromptsLeft(orgId, n),
            });
            if (ok) {
              toast('Coco AI credits updated.');
              await refresh();
            }
          },
        },
        button('−10', { size: 'sm', title: 'Remove 10 credits in the box. Nothing is saved until "Update credits".', onClick: () => step(-10) }),
        el('div', { class: 'field field--fixed', style: 'width:120px' }, el('label', {}, 'Credits left'), cocoInput),
        button('+10', { size: 'sm', title: 'Add 10 credits in the box. Nothing is saved until "Update credits".', onClick: () => step(10) }),
        button('Update credits', { variant: 'primary', type: 'submit' }),
        cocoError,
      ),
    );

    const statusRow = el(
      'div',
      { class: 'ctl' },
      el('div', {}, el('div', { class: 'ctl__title' }, 'Organisation status'), el('div', { class: 'ctl__desc' }, 'Deactivating blocks every user in this organisation on their next request.')),
      el(
        'div',
        { class: 'ctl__controls' },
        org.isActive ? badge('Active', 'green', { dot: true }) : badge('Inactive', 'gray', { dot: true }),
        org.isActive
          ? button('Deactivate', { variant: 'danger-outline', onClick: () => void setOrgStatus(false) })
          : button('Activate', { variant: 'primary', onClick: () => void setOrgStatus(true) }),
      ),
    );

    const dangerZone = card({
      title: 'Danger zone',
      className: 'danger-zone',
      body: el(
        'div',
        { class: 'danger-row' },
        el(
          'div',
          {},
          el('p', { class: 'ctl__title' }, 'Delete this organisation'),
          el(
            'p',
            { class: 'danger-row__desc' },
            'A soft delete — users keep their accounts and other memberships, and outstanding invite links are cancelled. It can be restored.',
          ),
        ),
        button('Delete organisation', {
          variant: 'danger',
          title: `Start deleting ${org.name}. Opens a confirmation that asks you to type the organisation name — nothing is deleted by this click alone.`,
          onClick: async () => {
            const ok = await typedConfirm({
              tone: 'red',
              iconName: 'modal-question',
              title: `Delete ${org.name}?`,
              text: 'Hides the tenant, closes every membership in it and cancels its outstanding invite links. Members keep their accounts and any other organisations they belong to. Reversible with "Restore organisation".',
              inputLabel: `Type "${org.name}" to confirm`,
              expected: org.name,
              confirmLabel: 'Delete organisation',
              onConfirm: (typed) => api.deleteOrg(orgId, typed),
            });
            if (ok) {
              toast(`${org.name} deleted. It can be restored from this page.`);
              await refresh();
              members?.reload();
            }
          },
        }),
      ),
    });

    return el(
      'div',
      { class: 'stack' },
      card({
        title: 'Trial & settings',
        subtitle: "Changes take effect on each member's next request.",
        body: el('div', {}, statusRow, planRow, org.plan === 'CMAI_CORE' ? coreWindowRow : trialRow, maxProjectsRow, maxUsersRow, revalidationsRow, cocoRow),
      }),
      dangerZone,
    );
  }

  // --- load ---------------------------------------------------------------------------

  function renderAll(org) {
    setCrumbs([{ label: org.name }]);
    renderHeader(org);
    renderKpis(org);
    renderNotice(org);
    renderTabs();
    renderPanel();
  }

  async function refresh() {
    try {
      const org = await api.getOrg(orgId);
      state.org = org;
      renderAll(org);
    } catch (err) {
      toastError(err instanceof ApiError ? err : null, 'Failed to reload the organisation.');
    }
  }

  async function initialLoad() {
    render(root, el('div', { class: 'kpi-row' }, Array.from({ length: 4 }, () => kpiCard({ label: '…', value: el('span', { class: 'skeleton skeleton--mid', style: 'height:24px;width:80px' }) }))));
    try {
      const org = await api.getOrg(orgId);
      state.org = org;
      if (!TAB_IDS.includes(state.tab)) state.tab = 'overview';
      render(root, headSlot, kpiSlot, noticeSlot, tabsSlot, panelSlot);
      renderAll(org);
    } catch (err) {
      render(
        root,
        el('button', { type: 'button', class: 'back-link', onclick: () => navigate('/orgs') }, icon('arrow-left'), 'Back to Organisations'),
        card({
          body:
            err instanceof ApiError && err.status === 404
              ? emptyState({ title: 'Organisation not found', text: 'It may have been removed, or the link is wrong.', action: button('Back to Organisations', { onClick: () => navigate('/orgs') }) })
              : errorState({ title: 'Failed to load this organisation', text: errMessage(err, 'Check the API and try again.'), onRetry: () => void initialLoad() }),
        }),
      );
    }
  }

  void initialLoad();
  return root;
}
